/*******************************************************************************
* Licensed Materials - Property of HCL 
* (c) Copyright HCL Technologies Ltd. 2020. All Rights Reserved. 

* Note to U.S. Government Users Restricted Rights:
* Use, duplication or disclosure restricted by GSA ADP Schedule
**************************************************************/ /**
 * Component that creates a 3D dropdown with checkboxes
 */

import React, { useState } from "react";
import PropTypes from "prop-types";
import Text from "./Text";
import Table from "./Table";
import Checkbox from "./Checkbox";

function DropdownCheckboxes({
    id,
    disabled = false,
    width,
    height,
    checked,
    setChecked,
    choices,
    backgroundColor = "#363636",
    hoverColor = "#474747",
    fontColor = "#c6c6c6",
    textSize = "h5",
    textSizeDecimal,
    text,
    aframeClasses,
    ...otherAttributes
}) {
    const [isOpen, setIsOpen] = useState(false);
    let rows;
    textSizeDecimal = textSizeDecimal || 30 / width;

    const preparedData = () => {
        const data = choices.map((elem, i) => {
            return [
                (width, height) => (
                    <Checkbox
                        id={i + 1}
                        checked={checked[i]}
                        side={height * 0.6}
                        setChecked={setChecked}
                        aframeClasses={aframeClasses}
                    />
                ),

                elem
            ];
        });
        rows = data.length + 1;

        const isCheckAll =
            checked.reduce((p, c) => {
                return p + (c ? 1 : 0);
            }, 0) === checked.length;

        return [
            [
                (width, height) => (
                    <Checkbox
                        id={0}
                        checked={isCheckAll}
                        side={height * 0.6}
                        setChecked={setChecked}
                        aframeClasses={aframeClasses}
                    />
                ),

                (!isCheckAll ? "Select" : "Unselect") + " all"
            ],
            ...data
        ];
    };
    const drawDropDown = () => {
        return (
            <Table
                position={`0 ${
                    -(height * (choices.length + 1)) / 2 - height / 2
                }`}
                width={width}
                height={height * (choices.length + 1)}
                data={preparedData()}
                textSize={textSize}
                textSizeDecimal={textSizeDecimal}
                aframeClasses={aframeClasses}
                minRows={rows}
            />
        );
    };

    return (
        <a-entity class="DropdownCheckboxes" {...otherAttributes}>
            <a-plane
                class={aframeClasses}
                width={width}
                height={height}
                color={backgroundColor}
                opacity={disabled ? "0.5" : "1"}
                onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    if (disabled || e.target.id === "pane") return;
                    setIsOpen((old) => !old);
                }}
            >
                {!disabled && (
                    <a-plane
                        id="pane"
                        width={width / 6}
                        height={height * 0.6}
                        color={fontColor}
                        position={`${(-width / 2) * 0.9 + width / 12} 0 0.01`}
                        class={aframeClasses}
                        onClick={() => {
                            if (
                                checked.reduce((prev, curr) => {
                                    return prev + (curr ? 1 : 0);
                                }, 0)
                            )
                                setChecked(-1);
                        }}
                    >
                        <Text
                            text={
                                checked.reduce((prev, curr) => {
                                    return prev + (curr ? 1 : 0);
                                }, 0) || "0"
                            }
                            size={textSize}
                            sizeDecimal={textSizeDecimal}
                            align="left"
                            width={width / 10}
                            position={`${-width / 12} 0 0`}
                            color={backgroundColor}
                        />
                        {checked.reduce((prev, curr) => {
                            return prev + (curr ? 1 : 0);
                        }, 0) && (
                            <a-image
                                width={height * 0.3}
                                height={height * 0.3}
                                position={`${width / 12 - height * 0.3} 0 0.01`}
                                src="#CloseBlack"
                            />
                        )}
                    </a-plane>
                )}
                <Text
                    position={`${(-width / 2) * 0.4} 0 0.01`}
                    color={fontColor}
                    align="left"
                    width={width * 0.75}
                    text={text}
                    opacity={disabled ? "0.5" : "1"}
                    size={textSize}
                    sizeDecimal={textSizeDecimal}
                />
                <a-image
                    width={height * 0.6}
                    height={height * 0.6}
                    position={`${(width / 2) * 0.75} 0 0.01`}
                    src={isOpen ? "#SortingUp" : "#SortingDown"}
                />
            </a-plane>
            {isOpen && drawDropDown()}
        </a-entity>
    );
}

DropdownCheckboxes.propTypes = {
    choices: PropTypes.array.isRequired,
    setChecked: PropTypes.func.isRequired,
    width: PropTypes.number.isRequired,
    height: PropTypes.number.isRequired,
    id: PropTypes.any.isRequired
};

export default DropdownCheckboxes;
